import { useEffect, useState } from "react";
import { api, inr, fmtDate, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Folder, FolderOpen, Download, FileText, Link2, Unlink } from "lucide-react";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const label = (m) => `${MONTH_NAMES[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}`;

const KINDS = [
  { key: "sales-invoices", label: "Sales Invoices", noun: "invoice", doc: (i) => `${API_URL}/invoices/${i.id}/pdf` },
  { key: "customer-pos", label: "Customer POs", noun: "PO", doc: (i) => (i.file_id ? `${API_URL}/files/${i.file_id}/download` : null) },
  { key: "purchase-bills", label: "Purchase Bills", noun: "bill", doc: (i) => (i.file_id ? `${API_URL}/files/${i.file_id}/download` : null) },
  { key: "vendor-pos", label: "Vendor POs", noun: "PO", doc: (i) => `${API_URL}/purchase-orders/${i.id}/pdf` },
  { key: "debit-notes", label: "Debit Notes", noun: "debit note", doc: (i) => `${API_URL}/vendors/bills/${i.id}/debit-note-pdf` },
  { key: "credit-notes", label: "Credit Notes", noun: "credit note", doc: (i) => `${API_URL}/invoices/${i.id}/credit-note` },
];

const LinkDot = ({ item }) => item.connected ? (
  <span title="Fully connected: Customer PO · Vendor PO · Purchase Bill · Sales Invoice" data-testid="disk-connected">
    <Link2 size={14} className="text-emerald-600 flex-none" />
  </span>
) : (
  <span title={`Missing: ${(item.missing || []).join(", ")}`} data-testid="disk-not-connected">
    <Unlink size={14} className="text-slate-300 flex-none" />
  </span>
);

export default function Disk() {
  const [kind, setKind] = useState("sales-invoices");
  const [groups, setGroups] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const active = KINDS.find((k) => k.key === kind);

  useEffect(() => {
    setGroups(null);
    setOpenMonth(null);
    api.get(`/disk/folders/${kind}`).then((r) => setGroups(r.data)).catch(() => setGroups([]));
  }, [kind]);

  const years = [...new Set((groups || []).map((g) => g.month.slice(0, 4)))];

  return (
    <div className="space-y-6" data-testid="disk-page">
      <PageHeader title="Disk" subtitle="All documents organised in monthly folders — download a month as ZIP to share with your CA" />

      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)}
                  className={`text-xs font-semibold rounded-md px-3.5 py-2 border transition-colors duration-150 ${kind === k.key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`disk-tab-${k.key}`}>{k.label}</button>
        ))}
        <span className="ml-auto hidden sm:inline-flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Link2 size={12} className="text-emerald-600" /> fully linked</span>
          <span className="inline-flex items-center gap-1"><Unlink size={12} className="text-slate-300" /> link missing (hover for details)</span>
        </span>
      </div>

      {years.map((y) => (
        <div key={y}>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{y}</div>
          <div className="space-y-2">
            {groups.filter((g) => g.month.startsWith(y)).map((g) => (
              <div key={g.month} className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid={`disk-month-${g.month}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <button onClick={() => setOpenMonth(openMonth === g.month ? null : g.month)}
                          className="flex items-center gap-3 flex-1 text-left" data-testid={`disk-open-${g.month}`}>
                    {openMonth === g.month
                      ? <FolderOpen size={18} className="text-amber-500 flex-none" />
                      : <Folder size={18} className="text-amber-500 flex-none" />}
                    <span className="font-semibold text-slate-800 text-sm">{label(g.month)}</span>
                    <span className="text-xs text-slate-500">{g.count} {active.noun}{g.count === 1 ? "" : "s"} · {inr(g.total)}</span>
                  </button>
                  <a href={`${API_URL}/disk/folders/${kind}/${g.month}/zip`}
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 transition-colors duration-200"
                     data-testid={`disk-zip-${g.month}`}>
                    <Download size={13} /> Download ZIP
                  </a>
                </div>
                {openMonth === g.month && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {g.items.map((i) => {
                      const url = active.doc(i);
                      return (
                        <div key={i.id} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50/60" data-testid={`disk-item-${i.id}`}>
                          <FileText size={14} className="text-slate-400 flex-none" />
                          <span className="font-mono text-xs text-slate-700 w-32 truncate">{i.number || "—"}</span>
                          <span className="flex-1 text-slate-700 truncate">{i.party}</span>
                          <LinkDot item={i} />
                          <span className="text-xs text-slate-500 hidden md:block">{fmtDate(i.date)}</span>
                          <span className="tabular-nums text-slate-800 w-24 text-right">{inr(i.total)}</span>
                          {url ? (
                            <a href={url} target="_blank" rel="noreferrer"
                               className="text-blue-600 hover:text-blue-800 text-xs font-medium w-9 text-right">{kind === "customer-pos" || kind === "purchase-bills" ? "File" : "PDF"}</a>
                          ) : (
                            <span className="text-xs text-slate-300 w-9 text-right">—</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {groups && groups.length === 0 && (
        <div className="text-center py-16 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
          No documents yet — monthly folders appear here automatically.
        </div>
      )}
    </div>
  );
}
